import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { FaUser, FaEnvelope, FaPhone, FaUserShield } from "react-icons/fa";
import CustomInput from "../form/input/CustomInput";
import LabelArea from "../form/Label/LabelArea";
import { RoleList } from "../../Enum/RoleList";
import { AiOutlineUser } from "react-icons/ai";
import { LuPhone } from "react-icons/lu";

const UserForm = ({
  register,
  errors,
  handleSubmit,
  onSubmit,
  watch,
  loading,
  exiting,
  handleStatus,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const statusValue = watch("status", true);
  const userId = watch("id");

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await handleSubmit(onSubmit)(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = loading || isSubmitting;

  return (
    <form
      onSubmit={handleFormSubmit}
      className="flex flex-col h-full justify-between"
      style={{ minHeight: "400px" }}
    >
      <div className="flex flex-col gap-5">
        {/* Name */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-blue-300 p-2 rounded-full">
                  <AiOutlineUser className="text-white text-base" />
                </div>
                <span>Name</span>
              </div>
            }
          />
          <CustomInput
            label="Name"
            name="name"
            register={register}
            disabled={!statusValue || isDisabled}
            required={true}
            placeholder="Enter full name"
            errors={errors}
            maxLength={30}
          />
        </div>

        {/* Role */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-purple-300 p-2 rounded-full">
                  <FaUserShield className="text-white text-base" />
                </div>
                <span>Role</span>
              </div>
            }
          />
          <select
            disabled={exiting || isDisabled}
            {...register("role", { required: "Role is required" })}
            className={`w-full px-3 py-2 focus:outline-none border rounded-md ${
              errors.role ? "border-red-500" : "border-gray-300"
            } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <option value="">Select Role</option>
            {RoleList.map((list) => (
              <option key={list.value} value={list.value}>
                {list.label}
              </option>
            ))}
          </select>
          {errors.role && (
            <p className="text-red-500 text-[11px] mt-1">
              {errors.role.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-green-300 p-2 rounded-full">
                  <LuPhone className="text-white text-base" />
                </div>
                <span>Phone Number</span>
              </div>
            }
          />
          <CustomInput
            label="Phone Number"
            name="phoneNumber"
            type="text"
            register={register}
            required={true}
            disabled={!statusValue || isDisabled}
            placeholder="Enter 10-digit phone number"
            errors={errors}
            validation={{
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Phone must be exactly 10 digits",
              },
            }}
            maxLength={10}
            onKeyDown={(e) => {
              const allowedKeys = [
                "Backspace",
                "Tab",
                "ArrowLeft",
                "ArrowRight",
                "Delete",
              ];
              if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-2">
          <LabelArea
            label={
              <div className="flex items-center gap-2">
                <div className="bg-blue-300 p-2 rounded-full">
                  <FaEnvelope className="text-white text-base" />
                </div>
                <span>Email</span>
              </div>
            }
          />
          <CustomInput
            label="Email"
            name="email"
            type="email"
            disabled={exiting || isDisabled}
            register={register}
            required={true}
            placeholder="Enter email"
            errors={errors}
            validation={{
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address",
              },
            }}
          />
        </div>

        {/* Status Toggle */}
        {exiting && (
          <div className="flex items-center gap-16">
            <LabelArea label="Status" />
            {statusValue ? (
              <div className="flex items-center gap-2">
                <span className="text-sm select-none">Inactive</span>
                <label
                  htmlFor="status"
                  className={`relative inline-block w-10 h-5 rounded-full transition ${
                    isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  } bg-gray-300`}
                >
                  <input
                    type="checkbox"
                    {...register("status")}
                    id="status"
                    disabled={isDisabled}
                    className="toggle-checkbox hidden peer"
                    onChange={(e) => {
                      if (!e.target.checked && !isDisabled) {
                        handleStatus(userId);
                      }
                    }}
                  />
                  <div
                    className={`w-11 h-6 rounded-full absolute after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                      statusValue ? "bg-green-600" : "bg-red-500"
                    } peer-checked:after:translate-x-full`}
                  />
                </label>
                <span className="text-sm select-none">Active</span>
              </div>
            ) : (
              <div className="flex text-red-500 items-center gap-2">
                This user is deactive
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        {statusValue && (
          <button
            type="submit"
            disabled={isDisabled}
            className={`${
              isDisabled 
                ? "bg-slate-300 cursor-not-allowed opacity-50" 
                : "bg-primary hover:bg-primary-dark"
            } w-full text-white py-2 px-4 rounded-[10px] transition-all duration-200`}
          >
            {isDisabled ? "Processing..." : "Save User"}
          </button>
        )}
      </div>
    </form>
  );
};

export default UserForm;